import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Input,
  Button,
  IconButton,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiEye,
  FiTrash2,
  FiArrowLeft,
  FiUsers,
  FiMoreHorizontal,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiDownload,
  FiUser,
  FiHome,
  FiMapPin,
  FiMail,
  FiPhone,
  FiCalendar,
  FiZap,
  FiDroplet,
  FiSun,
  FiTrendingUp,
  FiBriefcase,
  FiLayers,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";

// ─── Palette "Linear / Vercel" (identique à Admin) ───
const c = {
  bg: "#FAFAFA",
  card: "#FFFFFF",
  border: "#E5E7EB",
  borderSubtle: "#F3F4F6",
  hover: "#F9FAFB",
  text: "#111827",
  textSec: "#6B7280",
  textMuted: "#9CA3AF",
  blue: "#2563EB",
  blueHover: "#1D4ED8",
  blueLight: "#EFF6FF",
  green: "#10B981",
  greenLight: "#ECFDF5",
  red: "#EF4444",
  redHover: "#DC2626",
  redLight: "#FEF2F2",
  amber: "#F59E0B",
  amberLight: "#FFFBEB",
  purple: "#8B5CF6",
  purpleLight: "#F5F3FF",
  pink: "#EC4899",
  pinkLight: "#FDF2F8",
  teal: "#14B8A6",
  tealLight: "#F0FDFA",
};

const FONT =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif";

// ─── Sous-composants ───

const StatPill = ({ label, value, color, icon: Icon }) => (
  <HStack
    spacing={2.5}
    px={3}
    py={2}
    bg="white"
    border="1px solid"
    borderColor={c.border}
    borderRadius="8px"
    minW="130px"
  >
    <Flex
      w="28px"
      h="28px"
      borderRadius="6px"
      bg={color.bg}
      color={color.text}
      align="center"
      justify="center"
      flexShrink={0}
    >
      <Icon size={14} />
    </Flex>
    <Box>
      <Text fontSize="11px" color={c.textSec} fontWeight="500" lineHeight="1">
        {label}
      </Text>
      <Text fontSize="18px" fontWeight="600" color={c.text} lineHeight="1.2" mt={0.5}>
        {value}
      </Text>
    </Box>
  </HStack>
);

// Row dans le modal (champ : valeur)
const DetailRow = ({ label, value, mono = false }) => {
  const displayValue =
    value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <Flex
      py={2}
      px={3}
      justify="space-between"
      align="start"
      borderBottom="1px solid"
      borderColor={c.borderSubtle}
      _last={{ borderBottom: "none" }}
      gap={4}
    >
      <Text fontSize="12px" color={c.textSec} fontWeight="500" flexShrink={0}>
        {label}
      </Text>
      <Text
        fontSize="13px"
        color={c.text}
        fontWeight="500"
        textAlign="right"
        fontFamily={mono ? "'SF Mono', Menlo, monospace" : "inherit"}
        wordBreak="break-word"
      >
        {displayValue}
      </Text>
    </Flex>
  );
};

// Section de champs dans le modal
const DetailSection = ({ title, icon: Icon, color, children }) => (
  <Box
    bg="white"
    border="1px solid"
    borderColor={c.border}
    borderRadius="8px"
    overflow="hidden"
    mb={3}
  >
    <Flex
      align="center"
      px={3}
      py={2.5}
      bg={c.hover}
      borderBottom="1px solid"
      borderColor={c.border}
      gap={2}
    >
      {Icon && (
        <Flex
          w="22px"
          h="22px"
          borderRadius="5px"
          bg={color?.bg || c.blueLight}
          color={color?.text || c.blue}
          align="center"
          justify="center"
        >
          <Icon size={12} />
        </Flex>
      )}
      <Text
        fontSize="11px"
        fontWeight="700"
        color={c.text}
        textTransform="uppercase"
        letterSpacing="0.4px"
      >
        {title}
      </Text>
    </Flex>
    <Box>{children}</Box>
  </Box>
);

// ─── Composant principal ───

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("clients"); // "clients" | "ite"
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const detailModal = useDisclosure();
  const deleteModal = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const navigate = useNavigate();
  const toast = useToast();

  const showToast = (title, status = "success", description) =>
    toast({
      title,
      description,
      status,
      duration: 2500,
      isClosable: true,
      position: "bottom-right",
      variant: "subtle",
    });

  const isITE = view === "ite";

  // ─── Fetch ───
  const fetchData = async (page) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const endpoint = isITE ? "/clientsITE" : "/clients";
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}${endpoint}?page=${page}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = isITE ? response.data.clientsITE : response.data.clients;
      setClients(list || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Erreur chargement", error);
      showToast("Erreur de chargement", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, view]);

  // ─── Search ───
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchTerm.trim()) {
      fetchData(currentPage);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const endpoint = isITE ? "/clientsITE/nom" : "/clients/nom";
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}${endpoint}`,
        {
          params: { nom: searchTerm },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const list = isITE ? response.data.clientsITE : response.data.clients;
      setClients(list || []);
      setSelected(new Set());
    } catch (error) {
      console.error("Erreur recherche", error);
      showToast("Erreur de recherche", "error");
    }
  };

  // ─── Delete ───
  const requestDelete = (client) => {
    setDeleteTarget(client);
    deleteModal.onOpen();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const endpoint = isITE ? "/clientsITE" : "/clients";
      const nameParam = isITE ? "nom" : "name";
      await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}${endpoint}?${nameParam}=${encodeURIComponent(
          deleteTarget.nom
        )}`
      );
      showToast(
        isITE ? "Étude ITE supprimée" : "Client supprimé",
        "info"
      );
      deleteModal.onClose();
      setDeleteTarget(null);
      if (detailModal.isOpen) detailModal.onClose();
      fetchData(currentPage);
    } catch (error) {
      console.error("Erreur suppression", error);
      showToast("Erreur de suppression", "error");
    }
  };

  // ─── Selection ───
  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === clients.length && clients.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(clients.map((c) => c._id)));
    }
  };

  // ─── Row click ───
  const handleRowClick = (client) => {
    setSelectedClient(client);
    detailModal.onOpen();
  };

  // ─── PDF Export ───
  const handleExportPDF = () => {
    if (!selectedClient) return;
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(
      isITE ? "Étude ITE" : "Fiche Client",
      15,
      18
    );
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`${selectedClient.nom || ""} ${selectedClient.prenom || ""}`, 15, 26);
    doc.setTextColor(0);
    doc.setFontSize(11);

    let y = 38;
    const fields = isITE ? iteFields(selectedClient) : clientFields(selectedClient);
    fields.forEach(([label, value]) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      const val = value === null || value === undefined || value === "" ? "-" : String(value);
      doc.setFont(undefined, "bold");
      doc.text(`${label} :`, 15, y);
      doc.setFont(undefined, "normal");
      doc.text(val, 80, y);
      y += 7;
    });

    doc.save(`${isITE ? "ITE" : "Client"}_${selectedClient.nom || "export"}.pdf`);
    showToast("PDF téléchargé");
  };

  // ─── Helpers ───
  const getInitials = (nom, prenom) => {
    const a = (nom || "").charAt(0).toUpperCase();
    const b = (prenom || "").charAt(0).toUpperCase();
    return (a + b) || "??";
  };
  const getAvatarColor = (nom) => {
    const palette = [
      { bg: c.blueLight, text: c.blue },
      { bg: c.greenLight, text: c.green },
      { bg: c.amberLight, text: c.amber },
      { bg: c.purpleLight, text: c.purple },
      { bg: c.pinkLight, text: c.pink },
      { bg: c.tealLight, text: c.teal },
    ];
    const hash = (nom || "").split("").reduce((a, ch) => a + ch.charCodeAt(0), 0);
    return palette[hash % palette.length];
  };
  const formatDate = (date) => {
    if (!date) return "—";
    try {
      return new Date(date).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };
  const formatEur = (n) => {
    if (n === null || n === undefined || n === "") return "—";
    const num = parseFloat(n);
    if (isNaN(num)) return String(n);
    return num.toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    });
  };

  const selectedCount = selected.size;
  const allSelected = selectedCount === clients.length && clients.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < clients.length;

  return (
    <Box minH="100vh" bg={c.bg} fontFamily={FONT} color={c.text}>
      {/* ─── Top navigation bar ─── */}
      <Flex
        as="header"
        h="56px"
        px={6}
        bg="white"
        borderBottom="1px solid"
        borderColor={c.border}
        align="center"
        justify="space-between"
      >
        <HStack spacing={3}>
          <IconButton
            icon={<FiArrowLeft size={16} />}
            variant="ghost"
            size="sm"
            color={c.textSec}
            onClick={() => navigate("/")}
            aria-label="Retour"
            _hover={{ bg: c.hover, color: c.text }}
          />
          <Text fontSize="14px" color={c.textSec} fontWeight="500">
            Accueil
          </Text>
          <Text color={c.textMuted}>/</Text>
          <Text fontSize="14px" color={c.text} fontWeight="600">
            {isITE ? "Études ITE" : "Clients"}
          </Text>
        </HStack>
        <Text fontSize="12px" color={c.textMuted}>
          {clients.length} affiché{clients.length > 1 ? "s" : ""} · Page {currentPage} / {totalPages}
        </Text>
      </Flex>

      {/* ─── Main content ─── */}
      <Box maxW="1400px" mx="auto" px={6} py={6}>
        {/* Page header */}
        <Flex align="flex-end" justify="space-between" mb={5} flexWrap="wrap" gap={4}>
          <Box>
            <Text fontSize="24px" fontWeight="700" color={c.text} letterSpacing="-0.4px">
              {isITE ? "Études ITE" : "Base clients"}
            </Text>
            <Text fontSize="13px" color={c.textSec} mt={1}>
              {isITE
                ? "Études d'isolation thermique par l'extérieur"
                : "Audits énergétiques (pompe à chaleur, ballon thermo, panneaux solaires)"}
            </Text>
          </Box>
          <HStack spacing={2.5}>
            <StatPill
              label={isITE ? "Études" : "Clients"}
              value={clients.length}
              color={{ bg: c.blueLight, text: c.blue }}
              icon={isITE ? FiLayers : FiUsers}
            />
            <StatPill
              label="Page"
              value={`${currentPage}/${totalPages}`}
              color={{ bg: c.amberLight, text: c.amber }}
              icon={FiFileText}
            />
          </HStack>
        </Flex>

        {/* ─── Segmented control Clients / ITE ─── */}
        <Flex
          bg="white"
          border="1px solid"
          borderColor={c.border}
          borderRadius="8px"
          p={1}
          mb={4}
          display="inline-flex"
        >
          <Button
            size="sm"
            variant="ghost"
            bg={view === "clients" ? c.hover : "transparent"}
            color={view === "clients" ? c.text : c.textSec}
            fontWeight="600"
            fontSize="13px"
            borderRadius="6px"
            h="30px"
            px={3}
            leftIcon={<FiUsers size={13} />}
            _hover={{ bg: c.hover, color: c.text }}
            onClick={() => {
              setView("clients");
              setCurrentPage(1);
              setSearchTerm("");
            }}
          >
            Clients
          </Button>
          <Button
            size="sm"
            variant="ghost"
            bg={view === "ite" ? c.hover : "transparent"}
            color={view === "ite" ? c.text : c.textSec}
            fontWeight="600"
            fontSize="13px"
            borderRadius="6px"
            h="30px"
            px={3}
            leftIcon={<FiLayers size={13} />}
            _hover={{ bg: c.hover, color: c.text }}
            onClick={() => {
              setView("ite");
              setCurrentPage(1);
              setSearchTerm("");
            }}
          >
            Études ITE
          </Button>
        </Flex>

        {/* ─── Toolbar ─── */}
        <Flex
          bg="white"
          border="1px solid"
          borderColor={c.border}
          borderRadius="8px"
          p={2.5}
          mb={0}
          align="center"
          gap={2}
          borderBottomRadius={0}
          borderBottom="none"
        >
          {/* Search form */}
          <form onSubmit={handleSearch} style={{ flex: 1, maxWidth: "400px" }}>
            <Flex
              bg={c.hover}
              borderRadius="6px"
              px={3}
              align="center"
              height="34px"
              border="1px solid"
              borderColor="transparent"
              _focusWithin={{ borderColor: c.blue, bg: "white" }}
              transition="all 0.15s"
            >
              <Box color={c.textMuted} mr={2} display="flex" alignItems="center">
                <FiSearch size={14} />
              </Box>
              <Input
                placeholder="Rechercher par nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="unstyled"
                fontSize="13px"
                color={c.text}
                _placeholder={{ color: c.textMuted }}
              />
              {searchTerm && (
                <Box
                  as="button"
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    fetchData(currentPage);
                  }}
                  color={c.textMuted}
                  fontSize="11px"
                  _hover={{ color: c.text }}
                >
                  Effacer
                </Box>
              )}
            </Flex>
          </form>

          <Button
            size="sm"
            variant="outline"
            borderColor={c.border}
            color={c.text}
            borderRadius="6px"
            fontSize="13px"
            fontWeight="500"
            h="34px"
            leftIcon={<FiSearch size={13} />}
            _hover={{ bg: c.hover, borderColor: c.textMuted }}
            onClick={handleSearch}
          >
            Rechercher
          </Button>

          <Box flex={1} />

          {/* Bulk actions */}
          {selectedCount > 0 && (
            <HStack
              spacing={2}
              bg={c.blueLight}
              border="1px solid"
              borderColor={c.blue}
              borderRadius="6px"
              px={3}
              h="34px"
            >
              <Text fontSize="12px" color={c.blue} fontWeight="600">
                {selectedCount} sélectionné{selectedCount > 1 ? "s" : ""}
              </Text>
            </HStack>
          )}
        </Flex>

        {/* ─── Data table ─── */}
        <Box
          bg="white"
          border="1px solid"
          borderColor={c.border}
          borderTopRadius={0}
          borderBottomRadius="8px"
          overflow="hidden"
        >
          {/* Table header */}
          <Flex
            bg={c.hover}
            borderBottom="1px solid"
            borderColor={c.border}
            h="40px"
            align="center"
            px={4}
            fontSize="11px"
            fontWeight="600"
            color={c.textSec}
            textTransform="uppercase"
            letterSpacing="0.4px"
          >
            <Box w="36px">
              <Checkbox
                isChecked={allSelected}
                isIndeterminate={someSelected}
                onChange={toggleSelectAll}
                colorScheme="blue"
                size="sm"
              />
            </Box>
            <Box flex="1 1 220px">Client</Box>
            <Box flex="1 1 180px">Contact</Box>
            <Box flex="1 1 200px">Adresse</Box>
            <Box w="140px">Commercial</Box>
            <Box w="110px">Date</Box>
            <Box w="80px" textAlign="right">
              Actions
            </Box>
          </Flex>

          {/* Table rows */}
          {isLoading ? (
            <Flex py={16} justify="center" color={c.textMuted}>
              <Text fontSize="13px">Chargement...</Text>
            </Flex>
          ) : clients.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={16}
              color={c.textMuted}
            >
              <Box mb={3}>
                <FiUsers size={32} />
              </Box>
              <Text fontSize="14px" fontWeight="500" color={c.textSec}>
                {searchTerm
                  ? `Aucun résultat pour "${searchTerm}"`
                  : isITE
                  ? "Aucune étude ITE"
                  : "Aucun client"}
              </Text>
              <Text fontSize="12px" color={c.textMuted} mt={1}>
                Les {isITE ? "études" : "clients"} créés via le simulateur apparaîtront ici
              </Text>
            </Flex>
          ) : (
            clients.map((client, idx) => {
              const isSelected = selected.has(client._id);
              const avatar = getAvatarColor(client.nom);
              const isLast = idx === clients.length - 1;
              const telephone = isITE ? client.tel1 : client.telephone;

              return (
                <Flex
                  key={client._id}
                  minH="56px"
                  align="center"
                  px={4}
                  borderBottom={isLast ? "none" : "1px solid"}
                  borderColor={c.borderSubtle}
                  bg={isSelected ? c.blueLight : "white"}
                  _hover={{ bg: isSelected ? c.blueLight : c.hover, cursor: "pointer" }}
                  transition="background 0.1s"
                  role="group"
                  onClick={() => handleRowClick(client)}
                >
                  {/* Checkbox */}
                  <Box w="36px" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      isChecked={isSelected}
                      onChange={(e) => toggleSelect(client._id, e)}
                      colorScheme="blue"
                      size="sm"
                    />
                  </Box>

                  {/* Client */}
                  <Flex flex="1 1 220px" align="center" minW={0}>
                    <Flex
                      w="34px"
                      h="34px"
                      borderRadius="6px"
                      bg={avatar.bg}
                      color={avatar.text}
                      align="center"
                      justify="center"
                      fontSize="12px"
                      fontWeight="600"
                      mr={3}
                      flexShrink={0}
                    >
                      {getInitials(client.nom, client.prenom)}
                    </Flex>
                    <Box minW={0}>
                      <Text
                        fontSize="13px"
                        fontWeight="600"
                        color={c.text}
                        isTruncated
                        lineHeight="1.3"
                      >
                        {client.nom} {client.prenom}
                      </Text>
                      <Text fontSize="11px" color={c.textMuted} isTruncated lineHeight="1.3">
                        {client.email || "—"}
                      </Text>
                    </Box>
                  </Flex>

                  {/* Contact */}
                  <Box flex="1 1 180px" minW={0}>
                    <Flex align="center" gap={1.5} color={c.text} fontSize="12px">
                      <FiPhone size={11} color={c.textMuted} />
                      <Text isTruncated fontFamily="'SF Mono', Menlo, monospace">
                        {telephone || "—"}
                      </Text>
                    </Flex>
                    {client.ville && (
                      <Flex align="center" gap={1.5} color={c.textMuted} fontSize="11px" mt={0.5}>
                        <FiMapPin size={10} />
                        <Text isTruncated>{client.ville}</Text>
                      </Flex>
                    )}
                  </Box>

                  {/* Adresse */}
                  <Box flex="1 1 200px" minW={0}>
                    <Text fontSize="12px" color={c.text} isTruncated>
                      {client.adresse || "—"}
                    </Text>
                    {client.departement && (
                      <Text fontSize="11px" color={c.textMuted} isTruncated>
                        {client.departement} · {client.region}
                      </Text>
                    )}
                  </Box>

                  {/* Commercial */}
                  <Box w="140px" minW={0}>
                    {client.commercial ? (
                      <HStack
                        spacing={1.5}
                        display="inline-flex"
                        bg={c.purpleLight}
                        color={c.purple}
                        px={2}
                        py={1}
                        borderRadius="4px"
                        border="1px solid #DDD6FE"
                        maxW="100%"
                      >
                        <FiBriefcase size={10} />
                        <Text fontSize="11px" fontWeight="600" isTruncated>
                          {client.commercial}
                        </Text>
                      </HStack>
                    ) : (
                      <Text fontSize="11px" color={c.textMuted}>
                        —
                      </Text>
                    )}
                  </Box>

                  {/* Date */}
                  <Box w="110px">
                    <Text fontSize="12px" color={c.textSec}>
                      {formatDate(client.dateSaved || client.createdAt)}
                    </Text>
                  </Box>

                  {/* Actions */}
                  <HStack
                    w="80px"
                    justify="flex-end"
                    spacing={0.5}
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    transition="opacity 0.15s"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      icon={<FiEye size={13} />}
                      size="xs"
                      variant="ghost"
                      color={c.textSec}
                      onClick={() => handleRowClick(client)}
                      aria-label="Voir"
                      _hover={{ color: c.blue, bg: c.blueLight }}
                    />
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreHorizontal size={14} />}
                        size="xs"
                        variant="ghost"
                        color={c.textSec}
                        aria-label="Plus d'actions"
                        _hover={{ color: c.text, bg: c.borderSubtle }}
                      />
                      <MenuList fontSize="13px" minW="180px">
                        <MenuItem
                          icon={<FiEye size={13} />}
                          onClick={() => handleRowClick(client)}
                        >
                          Voir les détails
                        </MenuItem>
                        <MenuItem
                          icon={<FiDownload size={13} />}
                          onClick={() => {
                            setSelectedClient(client);
                            setTimeout(handleExportPDF, 50);
                          }}
                        >
                          Exporter en PDF
                        </MenuItem>
                        <Divider />
                        <MenuItem
                          icon={<FiTrash2 size={13} />}
                          color={c.red}
                          onClick={() => requestDelete(client)}
                        >
                          Supprimer
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Flex>
              );
            })
          )}
        </Box>

        {/* ─── Pagination footer ─── */}
        <Flex
          justify="space-between"
          align="center"
          mt={4}
          fontSize="12px"
          color={c.textMuted}
        >
          <Text>
            {clients.length} {isITE ? "étude" : "client"}
            {clients.length > 1 ? "s" : ""} sur cette page
          </Text>
          <HStack spacing={1}>
            <IconButton
              icon={<FiChevronLeft size={14} />}
              size="sm"
              variant="outline"
              borderColor={c.border}
              color={c.text}
              isDisabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              aria-label="Page précédente"
              _hover={{ bg: c.hover }}
            />
            <Text px={3} fontSize="12px" color={c.text} fontWeight="500">
              Page {currentPage} / {totalPages}
            </Text>
            <IconButton
              icon={<FiChevronRight size={14} />}
              size="sm"
              variant="outline"
              borderColor={c.border}
              color={c.text}
              isDisabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Page suivante"
              _hover={{ bg: c.hover }}
            />
          </HStack>
        </Flex>
      </Box>

      {/* ─── Detail Modal ─── */}
      <Modal
        isOpen={detailModal.isOpen}
        onClose={detailModal.onClose}
        size="2xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay bg="rgba(17, 24, 39, 0.5)" backdropFilter="blur(4px)" />
        <ModalContent
          borderRadius="10px"
          border="1px solid"
          borderColor={c.border}
          maxH="85vh"
        >
          <ModalHeader pb={3} pt={4} px={5}>
            {selectedClient && (
              <Flex align="center" gap={3}>
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="8px"
                  bg={getAvatarColor(selectedClient.nom).bg}
                  color={getAvatarColor(selectedClient.nom).text}
                  align="center"
                  justify="center"
                  fontSize="14px"
                  fontWeight="600"
                >
                  {getInitials(selectedClient.nom, selectedClient.prenom)}
                </Flex>
                <Box>
                  <Text fontSize="16px" fontWeight="600" color={c.text}>
                    {selectedClient.nom} {selectedClient.prenom}
                  </Text>
                  <Text fontSize="12px" fontWeight="400" color={c.textSec}>
                    {isITE ? "Étude ITE" : "Audit énergétique"} ·{" "}
                    {formatDate(selectedClient.dateSaved || selectedClient.createdAt)}
                  </Text>
                </Box>
              </Flex>
            )}
          </ModalHeader>
          <ModalCloseButton color={c.textSec} top={4} />
          <ModalBody px={5} py={3} bg={c.bg}>
            {selectedClient && (isITE ? (
              <ITEDetailView client={selectedClient} formatEur={formatEur} />
            ) : (
              <ClientDetailView client={selectedClient} formatEur={formatEur} />
            ))}
          </ModalBody>
          <ModalFooter gap={2} borderTop="1px solid" borderColor={c.border} py={3}>
            <Button
              size="sm"
              variant="outline"
              borderColor={c.border}
              color={c.text}
              onClick={detailModal.onClose}
              fontSize="13px"
              fontWeight="500"
              _hover={{ bg: c.hover }}
            >
              Fermer
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor={c.border}
              color={c.text}
              onClick={handleExportPDF}
              fontSize="13px"
              fontWeight="500"
              leftIcon={<FiDownload size={13} />}
              _hover={{ bg: c.hover }}
            >
              Exporter PDF
            </Button>
            <Box flex={1} />
            <Button
              size="sm"
              bg={c.red}
              color="white"
              onClick={() => {
                detailModal.onClose();
                requestDelete(selectedClient);
              }}
              fontSize="13px"
              fontWeight="600"
              _hover={{ bg: c.redHover }}
              leftIcon={<FiTrash2 size={13} />}
            >
              Supprimer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ─── Delete Confirmation Modal ─── */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => {
          deleteModal.onClose();
          setDeleteTarget(null);
        }}
        size="md"
        isCentered
      >
        <ModalOverlay bg="rgba(17, 24, 39, 0.5)" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="10px" border="1px solid" borderColor={c.border}>
          <ModalHeader fontSize="16px" fontWeight="600" color={c.text} pb={2}>
            <HStack spacing={2}>
              <Flex
                w="32px"
                h="32px"
                borderRadius="8px"
                bg={c.redLight}
                color={c.red}
                align="center"
                justify="center"
              >
                <FiTrash2 size={16} />
              </Flex>
              <Text>Confirmer la suppression</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={c.textSec} />
          <ModalBody>
            <Text fontSize="14px" color={c.text} mb={2}>
              Supprimer{" "}
              <Text as="span" fontWeight="600">
                {deleteTarget?.nom} {deleteTarget?.prenom}
              </Text>{" "}
              ?
            </Text>
            <Text fontSize="13px" color={c.textSec}>
              Cette action est irréversible. {isITE ? "L'étude ITE" : "La fiche client"} et toutes ses données seront définitivement supprimées.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="outline"
              size="sm"
              borderColor={c.border}
              color={c.text}
              onClick={() => {
                deleteModal.onClose();
                setDeleteTarget(null);
              }}
              fontSize="13px"
              fontWeight="500"
              _hover={{ bg: c.hover }}
            >
              Annuler
            </Button>
            <Button
              size="sm"
              bg={c.red}
              color="white"
              onClick={confirmDelete}
              fontSize="13px"
              fontWeight="600"
              _hover={{ bg: c.redHover }}
              leftIcon={<FiTrash2 size={13} />}
            >
              Supprimer définitivement
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// ─── Vues détail (sections organisées) ───

const ClientDetailView = ({ client, formatEur }) => (
  <>
    <DetailSection title="Informations personnelles" icon={FiUser} color={{ bg: c.blueLight, text: c.blue }}>
      <DetailRow label="Civilité" value={client.sexe} />
      <DetailRow label="Nom" value={client.nom} />
      <DetailRow label="Prénom" value={client.prenom} />
      <DetailRow label="Email" value={client.email} />
      <DetailRow label="Téléphone" value={client.telephone} mono />
      <DetailRow label="Commercial" value={client.commercial} />
    </DetailSection>

    <DetailSection title="Localisation" icon={FiMapPin} color={{ bg: c.tealLight, text: c.teal }}>
      <DetailRow label="Adresse" value={client.adresse} />
      <DetailRow label="Ville" value={client.ville} />
      <DetailRow label="Département" value={client.departement} />
      <DetailRow label="Région" value={client.region} />
      <DetailRow label="Province / IDF" value={client.provinceouIdf} />
    </DetailSection>

    <DetailSection title="Foyer" icon={FiHome} color={{ bg: c.amberLight, text: c.amber }}>
      <DetailRow label="Nombre d'occupants" value={client.numberOfOccupants} />
      <DetailRow label="Revenu fiscal" value={formatEur(client.revenuFiscal)} />
    </DetailSection>

    {(client.pompeSelected || client.coutProjetPompe) && (
      <DetailSection title="Projet Pompe à chaleur" icon={FiZap} color={{ bg: c.greenLight, text: c.green }}>
        <DetailRow label="Pompe sélectionnée" value={client.pompeSelected} />
        <DetailRow label="Modèle" value={client.pompeModeleSelected} />
        <DetailRow label="Conso annuelle" value={client.consoAnnuellePompe ? `${client.consoAnnuellePompe} kWh` : "—"} />
        <DetailRow label="Dépense future" value={formatEur(client.depenseFuturePompe)} />
        <DetailRow label="Coût projet" value={formatEur(client.coutProjetPompe)} />
        <DetailRow label="MaPrimeRénov'" value={formatEur(client.maPrimePompe)} />
        <DetailRow label="Aides CEE" value={formatEur(client.aidesCeePompe)} />
      </DetailSection>
    )}

    {client.coutProjetBallon > 0 && (
      <DetailSection title="Projet Ballon thermodynamique" icon={FiDroplet} color={{ bg: c.blueLight, text: c.blue }}>
        <DetailRow label="Conso annuelle" value={client.consoAnnuelleBallon ? `${client.consoAnnuelleBallon} kWh` : "—"} />
        <DetailRow label="Dépense future" value={formatEur(client.depenseFutureBallon)} />
        <DetailRow label="Coût projet" value={formatEur(client.coutProjetBallon)} />
        <DetailRow label="MaPrimeRénov'" value={formatEur(client.maPrimeBallon)} />
        <DetailRow label="Aides CEE" value={formatEur(client.aidesCeeBallon)} />
      </DetailSection>
    )}

    {client.panneauxSelected && (
      <DetailSection title="Projet Panneaux solaires" icon={FiSun} color={{ bg: c.amberLight, text: c.amber }}>
        <DetailRow label="Nombre de panneaux" value={client.panneauxSelected} />
        <DetailRow label="Conso annuelle" value={client.consoAnnuellePanneaux ? `${client.consoAnnuellePanneaux} kWh` : "—"} />
        <DetailRow label="Production annuelle" value={client.productionAnnuellePanneaux ? `${client.productionAnnuellePanneaux} kWh` : "—"} />
        <DetailRow label="Dépense future" value={formatEur(client.depenseFuturePanneaux)} />
        <DetailRow label="Coût projet" value={formatEur(client.coutProjetPanneaux)} />
        <DetailRow label="Prime auto-conso" value={formatEur(client.primeAutoConsoPanneaux)} />
        <DetailRow label="Récupération TVA" value={formatEur(client.recuperationTvaPanneaux)} />
      </DetailSection>
    )}
  </>
);

const ITEDetailView = ({ client, formatEur }) => (
  <>
    <DetailSection title="Informations personnelles" icon={FiUser} color={{ bg: c.blueLight, text: c.blue }}>
      <DetailRow label="Civilité" value={client.sexe} />
      <DetailRow label="Nom" value={client.nom} />
      <DetailRow label="Prénom" value={client.prenom} />
      <DetailRow label="Email" value={client.email} />
      <DetailRow label="Téléphone" value={client.tel1} mono />
      <DetailRow label="Commercial" value={client.commercial} />
    </DetailSection>

    <DetailSection title="Localisation" icon={FiMapPin} color={{ bg: c.tealLight, text: c.teal }}>
      <DetailRow label="Adresse" value={client.adresse} />
      <DetailRow label="Ville" value={client.ville} />
      <DetailRow label="Département" value={client.departement} />
      <DetailRow label="Région" value={client.region} />
      <DetailRow label="Province / IDF" value={client.provinceOuIdf} />
    </DetailSection>

    <DetailSection title="Logement" icon={FiHome} color={{ bg: c.amberLight, text: c.amber }}>
      <DetailRow label="Nombre d'occupants" value={client.numberOfOccupants} />
      <DetailRow label="Année de construction" value={client.constructionYear} />
      <DetailRow label="Surface au sol" value={client.surfaceAuSol ? `${client.surfaceAuSol} m²` : "—"} />
      <DetailRow label="Surface habitable" value={client.surfaceHabitable ? `${client.surfaceHabitable} m²` : "—"} />
      <DetailRow label="Superficie des murs" value={client.superficieMur ? `${client.superficieMur} m²` : "—"} />
    </DetailSection>

    <DetailSection title="Projet ITE" icon={FiLayers} color={{ bg: c.purpleLight, text: c.purple }}>
      <DetailRow label="Dépense chauffage" value={formatEur(client.depenseChauffage)} />
      <DetailRow label="Coût projet ITE" value={formatEur(client.coutProjetITE)} />
      <DetailRow label="MaPrimeRénov' ITE" value={formatEur(client.maPrimeRenovITE)} />
      <DetailRow label="Aides CEE ITE" value={formatEur(client.aidesCeeITE)} />
    </DetailSection>

    <DetailSection title="Rentabilité" icon={FiTrendingUp} color={{ bg: c.greenLight, text: c.green }}>
      <DetailRow label="Économies sur 10 ans" value={formatEur(client.economies10ansITE)} />
    </DetailSection>
  </>
);

// ─── Helpers pour le PDF ───

const clientFields = (c) => [
  ["Commercial", c.commercial],
  ["Nom", c.nom],
  ["Prénom", c.prenom],
  ["Email", c.email],
  ["Adresse", c.adresse],
  ["Sexe", c.sexe],
  ["Province ou IDF", c.provinceouIdf],
  ["Ville", c.ville],
  ["Téléphone", c.telephone],
  ["Région", c.region],
  ["Département", c.departement],
  ["Nombre d'occupants", c.numberOfOccupants],
  ["Revenu fiscal", c.revenuFiscal],
  ["Pompe sélectionnée", c.pompeSelected],
  ["Modèle de pompe", c.pompeModeleSelected],
  ["Consommation annuelle pompe", c.consoAnnuellePompe],
  ["Dépense future pompe", c.depenseFuturePompe],
  ["Coût projet pompe", c.coutProjetPompe],
  ["MaPrimeRénov' pompe", c.maPrimePompe],
  ["Aides CEE pompe", c.aidesCeePompe],
  ["Conso annuelle ballon", c.consoAnnuelleBallon],
  ["Dépense future ballon", c.depenseFutureBallon],
  ["Coût projet ballon", c.coutProjetBallon],
  ["MaPrimeRénov' ballon", c.maPrimeBallon],
  ["Aides CEE ballon", c.aidesCeeBallon],
  ["Panneaux sélectionnés", c.panneauxSelected],
  ["Conso annuelle panneaux", c.consoAnnuellePanneaux],
  ["Production annuelle panneaux", c.productionAnnuellePanneaux],
  ["Dépense future panneaux", c.depenseFuturePanneaux],
  ["Coût projet panneaux", c.coutProjetPanneaux],
  ["Prime auto-conso panneaux", c.primeAutoConsoPanneaux],
  ["Récupération TVA panneaux", c.recuperationTvaPanneaux],
];

const iteFields = (c) => [
  ["Commercial", c.commercial],
  ["Nom", c.nom],
  ["Prénom", c.prenom],
  ["Email", c.email],
  ["Adresse", c.adresse],
  ["Sexe", c.sexe],
  ["Province ou IDF", c.provinceOuIdf],
  ["Ville", c.ville],
  ["Téléphone", c.tel1],
  ["Région", c.region],
  ["Département", c.departement],
  ["Nombre d'occupants", c.numberOfOccupants],
  ["Année de construction", c.constructionYear],
  ["Surface au sol", c.surfaceAuSol],
  ["Surface habitable", c.surfaceHabitable],
  ["Superficie mur", c.superficieMur],
  ["Dépense chauffage", c.depenseChauffage],
  ["Coût projet ITE", c.coutProjetITE],
  ["MaPrimeRénov' ITE", c.maPrimeRenovITE],
  ["Aides CEE ITE", c.aidesCeeITE],
  ["Économies 10 ans", c.economies10ansITE],
];

export default Clients;
